import { useEffect, useRef, useState } from "react";
import type { OnboardingState } from "../../../api/types.js";
import { AtomVisual } from "../../workspace/AtomVisual.js";
import { OnboardingThread } from "./OnboardingThread.js";
import { useOnboarding } from "./useOnboarding.js";

export function OnboardingPage({
  initialState,
  onCompleted,
  onSkipped,
  onStateChange
}: {
  initialState: OnboardingState;
  onCompleted: () => void;
  onSkipped: () => void;
  onStateChange: (state: OnboardingState) => void;
}) {
  const onboarding = useOnboarding(initialState, onStateChange);
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const skipButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // completed 不自动转场:停留在总结页,由完成条的“进入工作台”触发 onCompleted。
  useEffect(() => {
    if (onboarding.state.status === "skipped") onSkipped();
  }, [onSkipped, onboarding.state.status]);

  useEffect(() => {
    if (!skipDialogOpen) return;
    cancelButtonRef.current?.focus();
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !onboarding.skipping) {
        setSkipDialogOpen(false);
        skipButtonRef.current?.focus();
      }
    };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [onboarding.skipping, skipDialogOpen]);

  const closeSkipDialog = () => {
    if (onboarding.skipping) return;
    setSkipDialogOpen(false);
    skipButtonRef.current?.focus();
  };

  const confirmSkip = async () => {
    const next = await onboarding.skip();
    if (next?.status === "skipped") setSkipDialogOpen(false);
  };

  if (!onboarding.started && onboarding.state.messages.length === 0) {
    return (
      <main className="onboarding-page" data-testid="onboarding-welcome">
        <header className="onboarding-header"><span className="onboarding-brand">Truth Seeker</span></header>
        {onboarding.error && <p className="onboarding-error" role="alert">{onboarding.error}</p>}
        <section className="onboarding-welcome">
          <AtomVisual />
          <p className="onboarding-eyebrow">首次使用</p>
          <h1>让科研助理更懂你的工作方式</h1>
          <p>用几轮简短对话告诉我你的研究背景、回答偏好和希望协作的方式。你也可以稍后再设置。</p>
          <div className="onboarding-welcome-actions">
            <button type="button" className="onboarding-secondary" disabled={onboarding.skipping} onClick={() => { void onboarding.skip(); }}>
              {onboarding.skipping ? "正在退出…" : "跳过，直接开始"}
            </button>
            <button type="button" className="onboarding-primary" disabled={onboarding.skipping} onClick={() => { void onboarding.start(); }}>立即设置</button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="onboarding-page onboarding-thread-view">
      <header className="onboarding-header">
        <span className="onboarding-brand">Truth Seeker</span>
        <div className="onboarding-header-actions">
          <span>建立你的协作偏好</span>
          {onboarding.state.status === "pending" && (
            <button
              ref={skipButtonRef}
              type="button"
              className="onboarding-header-skip"
              disabled={onboarding.running || onboarding.skipping}
              onClick={() => setSkipDialogOpen(true)}
            >跳过</button>
          )}
        </div>
      </header>
      {onboarding.error && <p className="onboarding-error" role="alert">{onboarding.error}</p>}
      <OnboardingThread
        messages={onboarding.state.messages}
        liveMessage={onboarding.liveMessage}
        running={onboarding.running}
        completed={onboarding.state.status === "completed"}
        onEnterWorkspace={onCompleted}
        onSubmitAsk={(messageId, answers) => { void onboarding.submitAsk(messageId, answers); }}
        onContinueWithText={(message) => { void onboarding.continueWithText(message); }}
        onStop={() => { void onboarding.stop(); }}
      />
      {skipDialogOpen && (
        <div className="onboarding-skip-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeSkipDialog(); }}>
          <section className="onboarding-skip-dialog" role="dialog" aria-modal="true" aria-labelledby="onboarding-skip-title" aria-describedby="onboarding-skip-description">
            <h2 id="onboarding-skip-title">退出引导？</h2>
            <p id="onboarding-skip-description">你可以后续在个人设置里继续设置</p>
            {onboarding.error && <p className="onboarding-skip-error" role="alert">{onboarding.error}</p>}
            <div className="onboarding-skip-actions">
              <button type="button" className="onboarding-secondary" disabled={onboarding.skipping} onClick={() => { void confirmSkip(); }}>
                {onboarding.skipping ? "正在退出…" : "确定退出"}
              </button>
              <button ref={cancelButtonRef} type="button" className="onboarding-primary" disabled={onboarding.skipping} onClick={closeSkipDialog}>取消</button>
            </div>
          </section>
        </div>
      )}
    </main>
  );
}
